import { useMemo, type ComponentType } from 'react';

import type { Tab } from '../../../components/Tabs';
import { TabsView } from '../../../components/Tabs';
import type { Company } from '../../../domain/company';
import { TRAFFIC_SHARE_TAB_KEY } from '../../../domain/user';
import { useStoredTabState } from '../../../hooks/useStoredTabState';
import { translate } from '../../../services/i18n';
import { getTwoMonthsPeriodSinceDate } from '../../../services/timeFormat';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { SectionHeader } from '../SectionHeader';
import { StyledSectionView } from '../styles';

import { ChannelsShareView } from './ChannelsShareView';
import { CountryShareView } from './CountryShareView';
import { StyledTrafficTabsContainer } from './styles';

const createChannelsShareTabPanel = (company: Company): ComponentType => {
  const TrafficShareChannelsTabPanel = () => <ChannelsShareView company={company} />;
  TrafficShareChannelsTabPanel.displayName = 'TrafficShareChannelsTabPanel';
  return TrafficShareChannelsTabPanel;
};

export enum TrafficShareTab {
  COUNTRIES = 'countries',
  CHANNELS = 'channels',
}

export const TrafficShare = ({ company }: { company: Company }) => {
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const { onSelectTab, activeTab } = useStoredTabState<TrafficShareTab>({
    storageKey: TRAFFIC_SHARE_TAB_KEY,
    defaultTab: TrafficShareTab.COUNTRIES,
    tabs: Object.values(TrafficShareTab),
  });

  const handleSelectTab = (tabName: TrafficShareTab) => {
    onSelectTab(tabName);
    dispatchTrackEventWithLocation(
      'company data',
      'select traffic share by',
      `traffic share/${tabName}/${company.domain}`
    );
  };

  const tabs: Tab<TrafficShareTab>[] = useMemo(
    () => [
      {
        name: TrafficShareTab.COUNTRIES,
        label: translate('company.traffic_share.countries'),
        component: CountryShareView,
      },
      {
        name: TrafficShareTab.CHANNELS,
        label: translate('company.traffic_share.channels'),
        component: createChannelsShareTabPanel(company),
      },
    ],
    [company]
  );

  return (
    <StyledSectionView data-automation="TrafficShareSection">
      <SectionHeader
        title={translate('company.traffic_share')}
        dateInfo={getTwoMonthsPeriodSinceDate(company.yearMonth ?? '')}
        websource="all"
      />
      <StyledTrafficTabsContainer data-automation="TrafficTabs">
        <TabsView<TrafficShareTab>
          tabsPosition="top"
          variant="compact"
          activeTabName={activeTab}
          onSelect={handleSelectTab}
          tabs={tabs}
        />
      </StyledTrafficTabsContainer>
    </StyledSectionView>
  );
};
